import { useEffect, useRef, useState } from "react";

import { useWS, type WSEvent } from "../lib/ws";

interface LiveLogProps {
  sourceId: string;
  // Initial log text seeded from a completed run's GET /runs/{id}/log
  // response. Live events arrive after.
  initialText?: string;
  // Tells the viewer "this run is the one I want to track live". Live
  // events for a different runId scroll past as if streamed; the viewer
  // doesn't try to filter to one run because operators want to see context
  // when one run finishes and the next starts.
}

interface LogLine {
  ts: string;
  level: string;
  message: string;
  // Free-form key=value tail for context (host, err, etc.).
  context?: string;
}

const MAX_LINES = 5_000;

// LiveLog is a black terminal-style panel that pipes the agent's WebSocket
// crawl events into a scrolling log view. Auto-scrolls unless the user has
// scrolled up; clicking back to the bottom resumes auto-scroll.
export function LiveLog({ sourceId, initialText }: LiveLogProps) {
  const [lines, setLines] = useState<LogLine[]>(() =>
    initialText ? parseLog(initialText) : [],
  );
  const [autoscroll, setAutoscroll] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const { connected } = useWS({
    sourceId,
    onEvent: (ev) => {
      const line = wsEventToLine(ev);
      if (!line) return;
      setLines((prev) => {
        const next = [...prev, line];
        return next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next;
      });
    },
  });

  // Replace the buffer when initialText changes (operator clicked a
  // different completed run in the history table).
  useEffect(() => {
    if (initialText !== undefined) {
      setLines(parseLog(initialText));
    }
  }, [initialText]);

  // Autoscroll when new lines arrive, unless the user has scrolled up.
  useEffect(() => {
    if (!autoscroll) return;
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [lines, autoscroll]);

  // Detect manual scroll-up → pause autoscroll.
  function handleScroll() {
    const el = containerRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    setAutoscroll(nearBottom);
  }

  return (
    <section className="card overflow-hidden">
      <div className="flex items-center justify-between border-b border-rule px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
            Live log
          </span>
          {connected ? (
            <span className="chip-emerald">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Connected
            </span>
          ) : (
            <span className="chip-amber">Reconnecting…</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!autoscroll ? (
            <button
              onClick={() => {
                setAutoscroll(true);
                if (containerRef.current) {
                  containerRef.current.scrollTop = containerRef.current.scrollHeight;
                }
              }}
              className="btn-ghost text-[11px]"
            >
              Resume autoscroll ↓
            </button>
          ) : null}
          <button
            onClick={() => setLines([])}
            className="btn-ghost text-[11px]"
          >
            Clear
          </button>
        </div>
      </div>
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="h-[420px] overflow-y-auto bg-nav px-5 py-4 font-mono text-[12px] leading-[1.55] text-nav-ink"
      >
        {lines.length === 0 ? (
          <div className="text-nav-mute">
            Waiting for events. Run a crawl to see live output here.
          </div>
        ) : (
          <ul>
            {lines.map((line, i) => (
              <li key={i} className="whitespace-pre-wrap">
                <span className="text-nav-mute">{line.ts}</span>{" "}
                <span className={levelColor(line.level)}>[{line.level}]</span>{" "}
                <span>{line.message}</span>
                {line.context ? (
                  <span className="text-nav-mute"> {line.context}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function wsEventToLine(ev: WSEvent): LogLine | null {
  // Status changes don't fit the log line format — synthesize an INFO line
  // so operators see them in the stream. Entry events are too noisy at
  // 1000s/s, skip them here (the dashboard counts them via a different
  // subscription).
  if (ev.kind === "crawl.entry") return null;
  if (ev.kind === "crawl.status") {
    return {
      ts: shortTs(ev.at),
      level: "INFO",
      message: `crawl status → ${ev.status ?? ""}`,
    };
  }
  // crawl.log
  return {
    ts: shortTs(ev.at),
    level: (ev.level ?? "info").toUpperCase(),
    message: ev.message ?? "",
    context: dataToContext(ev.data),
  };
}

function dataToContext(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const entries = Object.entries(data as Record<string, unknown>);
  if (entries.length === 0) return undefined;
  return entries.map(([k, v]) => `${k}=${formatVal(v)}`).join(" ");
}

function formatVal(v: unknown): string {
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

function shortTs(iso: string): string {
  // 12:34:56.789 — wall-clock readable, ms precision matters for chasing
  // ordering across rapid log lines.
  try {
    const d = new Date(iso);
    return d.toISOString().slice(11, 23);
  } catch {
    return iso;
  }
}

function levelColor(level: string): string {
  switch (level) {
    case "ERROR":
      return "text-rose-500";
    case "WARN":
      return "text-amber-500";
    case "INFO":
      return "text-brand-300";
    case "DEBUG":
      return "text-nav-mute";
    default:
      return "text-nav-mute";
  }
}

// parseLog converts the runner-written log file format back into LogLine[]
// for replay. Tolerant — unparseable lines fall through as INFO with the
// raw text as message so we don't drop content.
function parseLog(text: string): LogLine[] {
  const out: LogLine[] = [];
  for (const raw of text.split("\n")) {
    const line = raw.trimEnd();
    if (!line) continue;
    const match = /^(\S+)\s+\[(\w+)\]\s+(.*)$/.exec(line);
    if (match) {
      out.push({
        ts: shortTs(match[1]),
        level: match[2].toUpperCase(),
        message: match[3],
      });
    } else {
      out.push({ ts: "", level: "INFO", message: line });
    }
  }
  return out.length > MAX_LINES ? out.slice(out.length - MAX_LINES) : out;
}
